import { _ } from "../../../../core/polyglot/polyglot.js";
import { isString } from "../../../../core/helpers/string-util.js";
import { select } from "../../../../core/elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import KeyFigureChartWidget from "../../shared/KeyFigureChartWidget.js";

/**
 * `KeyFigureChartOptionsEditorWidget` renders a chart for a custom keyfigure,
 * with dropdowns to edit its chart options.
 *
 * @param {keyFigure} spec.keyFigure - KeyFigure to plot.
 */
export default class KeyFigureChartOptionsEditorWidget extends Widget {
	constructor({ keyFigure } = {}) {
		super(...arguments);

		this._keyFigure = keyFigure;
		this._previewColor = null;

		this.keyFigureChartOptionsChanged = this._events.createEvent();

		this._chart = new KeyFigureChartWidget({
			showTooltip: false,
			showDataLabels: true,
		});

		this._chart.initialize();
		if (keyFigure) this.setKeyFigure(keyFigure);
	}

	setKeyFigure(keyFigure) {
		this._keyFigure = keyFigure;
		this._initialPresetColor = null;
		this._initialPresetStyle = null;

		const options = keyFigure.getChartOptions();
		const isKnown = keyFigure.isKnown();

		if (this._areChartOptionsComplete(options) && isKnown)
			this._initPresetOptions();

		this.update();
	}

	setResult(result) {
		this._chart.setResult(result);
	}

	renderContentOn(html) {
		html.h2(
			{ klass: "chart-options-title" },
			_("Chart settings"),
			this._renderChartOptionsOn.bind(this),
		);

		html.render(this._chart);
	}

	_areChartOptionsComplete(options) {
		return (
			options?.type === "column" ||
			(options?.type === "line" && isString(options.dashStyle))
		);
	}

	_initPresetOptions() {
		const defaultStyles = [
			{ type: "line", dashStyle: "solid" },
			{ type: "line", dashStyle: "dot" },
			{ type: "line", dashStyle: "dash" },
			{ type: "column" },
		];

		const chartOptions = this._keyFigure.getChartOptions() || {};

		const isDefaultStyle = defaultStyles.some(
			({ type, dashStyle }) =>
				type === chartOptions.type &&
				(type !== "line" || dashStyle === chartOptions.dashStyle),
		);

		if (!isDefaultStyle) {
			this._initialPresetStyle = chartOptions;
		}

		const defaultColors = [
			"black",
			"#ffea00",
			"#ff6430",
			"#c5c5c5",
			"#00921b",
		];

		if (!defaultColors.includes(chartOptions.color)) {
			this._initialPresetColor = chartOptions.color;
		}
	}

	_renderChartOptionsOn(html) {
		html.div(
			{ klass: "chart-options" },
			this._renderChartColorOptionOn.bind(this),
			this._renderChartSeriesTypeOn.bind(this),
		);
	}

	_renderChartColorOptionOn(html) {
		const actualColor = this._keyFigure.getChartOptions().color;

		const defaultItems = [
			{ value: "black", label: _("Black") },
			{ value: "#ffea00", label: _("Yellow") },
			{ value: "#ff6430", label: _("Red") },
			{ value: "#c5c5c5", label: _("Grey") },
			{ value: "#00921b", label: _("Green") },
		];

		const isDefault = defaultItems.some(
			({ value }) => value === actualColor,
		);

		if (!isDefault && !this._initialPresetColor) {
			this._initialPresetColor = actualColor;
		}

		const items = [...defaultItems];
		if (
			this._initialPresetColor &&
			!items.find(({ value }) => value === this._initialPresetColor)
		) {
			items.push({
				value: this._initialPresetColor,
				label: _("Preset color"),
			});
		}

		html.div({
			klass: "preview",
			style: `background-color: ${this._previewColor || actualColor}`,
		});

		html.div(
			{ klass: "value-type-editor" },
			select({
				items,
				selected: actualColor,
				ref: (ref) => ref.setAttribute("name", "chart-color"),
				onChange: (color) => {
					this._setChartColorOption(color);
				},
			}),
		);
	}

	_renderChartSeriesTypeOn(html) {
		const actualStyle =
			this._initialPresetStyle || this._keyFigure.getChartOptions();

		const defaultItems = [
			{ value: { type: "line", dashStyle: "solid" }, label: _("Line") },
			{
				value: { type: "line", dashStyle: "dot" },
				label: _("Dotted line"),
			},
			{
				value: { type: "line", dashStyle: "dash" },
				label: _("Dashed line"),
			},
			{ value: { type: "column" }, label: _("Bar") },
		];

		const items = [...defaultItems];

		const styleAlreadyExists = items.some(
			({ value }) =>
				value.type === actualStyle.type &&
				value.dashStyle === actualStyle.dashStyle,
		);

		if (!styleAlreadyExists) {
			items.push({
				value: {
					type: actualStyle.type,
					dashStyle: actualStyle.dashStyle,
				},
				label: _("Preset style"),
			});
		}

		const selectedItem = items.find(
			({ value }) =>
				value.type === actualStyle?.type &&
				(value.dashStyle || null) === (actualStyle?.dashStyle || null),
		);

		html.div(
			{ klass: "value-type-editor" },
			select({
				items,
				selected: selectedItem.value,
				ref: (ref) => ref.setAttribute("name", "chart-type"),
				onChange: (style) => {
					this._setChartSeriesTypeOptions(style);
				},
			}),
		);
	}

	_setChartColorOption(color) {
		this._keyFigure.setChartColorOption(color);
		this._previewColor = color;
		this.keyFigureChartOptionsChanged.trigger();
		this.update();
	}

	_setChartSeriesTypeOptions(options) {
		this._keyFigure.setChartSeriesTypeOptions(options);
		this.keyFigureChartOptionsChanged.trigger();
	}
}
